import { isBefore, isSameDay } from 'date-fns';
import { getSortedGoals, useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { Goal } from 'shared/types/goal';
import { InsightSections } from 'shared/types/insights';
import { GoalGroupBy, GoalSortingMode } from 'shared/types/sorting-mode';
import { GoalRoadmapSortingKeys } from 'shared/types/user-settings';
import { sortGoals } from 'shared/utils/sort-goals';
import { sortItems } from 'shared/utils/sort-items';

export const useInsightsGoals = () => {
  const today = useToday();
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();
  const user = useUser();

  return useMemo(() => {
    const activeMainGoals = goals.filter(
      ({ archivedAt, completedAt, parentIds }) =>
        !archivedAt && !completedAt && !parentIds?.length,
    );

    let order =
      user?.settings?.goalRoadmapSorting?.[GoalRoadmapSortingKeys.InProgress] ??
      [];

    if (user?.settings?.goalGroupBy === GoalGroupBy.LifeArea) {
      const sortedLifeAreas = sortItems(
        lifeAreas,
        user?.settings?.lifeAreaSorting ?? [],
        'id',
      );
      order = [
        ...sortedLifeAreas
          .map(({ id }) => {
            const lifeAreaGoals = activeMainGoals.filter(
              ({ lifeAreaId }) => lifeAreaId === id,
            );
            return sortItems(
              lifeAreaGoals,
              user?.settings?.goalGroupSorting?.lifeArea?.sorting?.[id] ?? [],
              'id',
            ).map(({ id }) => id);
          })
          .flat(1),
        ...(user?.settings?.goalGroupSorting?.lifeArea?.sorting?.['null'] ??
          []),
      ];
    }

    let sortedMainGoals = getSortedGoals(activeMainGoals, {
      mode: [user?.settings?.goalSortingMode].filter(
        Boolean,
      ) as GoalSortingMode[],
      order,
      allGoals: goals,
    });

    if (
      user?.settings?.insights?.sections?.[InsightSections.Goals]?.order?.length
    ) {
      sortedMainGoals = sortItems(
        sortedMainGoals,
        user.settings.insights.sections[InsightSections.Goals].order,
        'id',
      );
    }

    // add missing active subGoals
    if (user?.settings?.insights?.showActiveSubGoals) {
      const processActiveGoalAndSubGoals = (goal: Goal) => {
        // check if goal is active to process it correctly
        const goalIsActive =
          (user?.settings?.insights?.showHiddenItems ||
            !user?.settings?.insights?.sections?.[
              InsightSections.Goals
            ]?.hiddenItems.includes(goal.id)) &&
          !goal.archivedAt &&
          !goal.completedAt &&
          !!goal.startDate &&
          (isBefore(goal.startDate, today) || isSameDay(goal.startDate, today));
        const results = goalIsActive ? [goal] : [];

        // get all subGoals to go through all levels and have the correct ordering
        const subGoals = goals.filter(
          ({ parentIds, archivedAt, completedAt }) =>
            !archivedAt &&
            !completedAt &&
            parentIds?.[parentIds.length - 1] === goal.id,
        );

        results.push(
          ...sortGoals(subGoals, {
            allGoals: goals,
            sortMode: goal.goalSortingMode,
            customOrder: goal.goalSorting,
          })
            .map((subGoal) => processActiveGoalAndSubGoals(subGoal))
            .flat(1),
        );
        return results;
      };

      // process all mainGoals
      return sortedMainGoals.reduce<Goal[]>((acc, goal) => {
        acc.push(...processActiveGoalAndSubGoals(goal));
        return acc;
      }, []);
    }

    // when no subGoals have to be shown, the logic for filtering out the inactive goals is not executed
    // (we need all main goals to display active subGoals of which the parent goal might not be active),
    // so we need to filter out the inactive goals here
    return sortedMainGoals.filter(
      ({ id, startDate }) =>
        (user?.settings?.insights?.showHiddenItems ||
          !user?.settings?.insights?.sections?.[
            InsightSections.Goals
          ]?.hiddenItems.includes(id)) &&
        !!startDate &&
        (isBefore(startDate, today) || isSameDay(startDate, today)),
    );
  }, [
    goals,
    lifeAreas,
    today,
    user?.settings?.goalGroupBy,
    user?.settings?.goalGroupSorting?.lifeArea?.sorting,
    user?.settings?.goalRoadmapSorting,
    user?.settings?.goalSortingMode,
    user?.settings?.insights?.sections,
    user?.settings?.insights?.showActiveSubGoals,
    user?.settings?.insights?.showHiddenItems,
    user?.settings?.lifeAreaSorting,
  ]);
};
